import { Button, ButtonSize, ButtonVariant, Iconify, Tooltip, SidebarModal, DocumentViewer } from '@repo/design-system/components/ui';
import PropTypes from 'prop-types';
import { useState } from 'react';
import "./ImagePreview.scss";

const ImagePreview = ({
    imagePreviews,
    handleClearClick,
    handleClearAll,
    isMultipleImageAllowed,
    isDownloadButton,
    isViewButton = true,
    onDownload,
    onView,
}) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const handleViewClick = (preview, index) => {
        const fileSource = typeof preview === 'string' ? preview : preview?.base64Data || preview?.url || preview?.src;
        const fileName = preview?.fileName || preview?.file?.name || preview?.name || `Image_${index + 1}.png`;

        const documentData = {
            base64Data: fileSource,
            file: {
                fileName: fileName,
                name: fileName,
            },
        };

        setSelectedDocument(documentData);
        setIsSidebarOpen(true);
        onView?.(index, documentData);
    };

    const handleSidebarClose = () => {
        setIsSidebarOpen(false);
        setSelectedDocument(null);
    };

    return (
        <div className="image-preview">
            {isMultipleImageAllowed && imagePreviews.length > 0 && (
                <div className="image-preview__header">
                    <h4 className="image-preview__title">Attached Images</h4>
                    <Button
                        className="image-preview__clear-all"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SM}
                        leftIcon="ic:outline-delete-sweep"
                        onClick={handleClearAll}
                        aria-label="Clear all images"
                    >
                        Clear All
                    </Button>
                </div>
            )}
            <div className="image-preview__grid">
                {imagePreviews.map((preview, index) => {
                    const previewSrc = typeof preview === 'string' ? preview : preview?.base64Data || preview?.url || preview?.src;
                    const removeTargetId = `remove-img-${index}`;
                    const downloadTargetId = `download-img-${index}`;
                    const viewTargetId = `view-img-${index}`;

                    return (
                        <div key={index} className="image-preview__item">
                            <div className="image-preview__media">
                                <img
                                    src={previewSrc}
                                    alt={`Preview ${index + 1}`}
                                    className="image-preview__img"
                                />

                                <div className="image-preview__actions">
                                    {isViewButton && (
                                        <>
                                            <Tooltip label="View" targetId={viewTargetId} />
                                            <button
                                                id={viewTargetId}
                                                aria-label="View image"
                                                type="button"
                                                className="image-preview__action-btn image-preview__action-btn--view"
                                                onClick={() => handleViewClick(preview, index)}
                                            >
                                                <Iconify width="auto" icon="ic:outline-visibility" />
                                            </button>
                                        </>
                                    )}

                                    <Tooltip label="Remove" targetId={removeTargetId} />
                                    <button
                                        id={removeTargetId}
                                        aria-label="Remove image"
                                        type="button"
                                        className="image-preview__action-btn image-preview__action-btn--delete"
                                        onClick={() => handleClearClick(index)}
                                    >
                                        <Iconify width="auto" icon="ic:outline-delete" />
                                    </button>

                                    {isDownloadButton && (
                                        <>
                                            <Tooltip label="Download" targetId={downloadTargetId} />
                                            <button
                                                id={downloadTargetId}
                                                className="image-preview__action-btn image-preview__action-btn--download"
                                                type="button"
                                                onClick={() => onDownload(index)}
                                                aria-label="Download image"
                                            >
                                                <Iconify width="auto" icon="ic:outline-download" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Document / Image Viewer Sidebar Modal */}
            <SidebarModal
                modalTitle={selectedDocument?.file?.fileName || "Image Preview"}
                isOpen={isSidebarOpen}
                onClose={handleSidebarClose}
                modalSize="w-50"
            >
                {selectedDocument && <DocumentViewer documentData={selectedDocument} />}
            </SidebarModal>
        </div>
    );
};

ImagePreview.propTypes = {
    imagePreviews: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
    handleClearClick: PropTypes.func.isRequired,
    handleClearAll: PropTypes.func,
    isMultipleImageAllowed: PropTypes.bool,
    isDownloadButton: PropTypes.bool,
    isViewButton: PropTypes.bool,
    onDownload: PropTypes.func,
    onView: PropTypes.func,
};

export default ImagePreview;
